import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useMemo } from 'react';
import { useStore } from 'react-redux';
import { useBoolean } from 'usehooks-ts';

import { getTaxInfo } from '@/api/tax-info';

import { DocumentActionItem } from '@/components/Form/ActionList/DocumentActionItem';
import { TaxInfoDrawer } from '@/components/PageForm/MoreInfoForm/drawers/TaxInfoDrawer/TaxInfoDrawer';
import {
  getTaxInfoStartingQuestionIndex,
  isTaxInfoComplete,
} from '@/components/PageForm/MoreInfoForm/drawers/TaxInfoDrawer/utils/taxInfoStatus';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { RootState } from '@/redux/store';
import {
  selectBasicInfoStatus,
  selectUserData,
} from '@/redux/user-data/selectors';

import {
  DocumentSection,
  DocumentStatus,
  DocumentType,
  RequiredDocumentSection,
} from '@/types/document';
import { User } from '@/types/user';

import { isExistingCustomer, isIdentityCheckComplete } from '@/utils/user';

import { RequiredDocumentSelectDrawer } from '../drawers/RequiredDocumentSelectDrawer';
import {
  getTaxSupportingDocumentUploadGuidance,
  groupTaxSupportingDocumentsByCountry,
} from '../utils/taxSupportingDocument';

function isTaxSupportingDocumentsPending(
  section?: RequiredDocumentSection,
): section is RequiredDocumentSection {
  return !!section && section.status === DocumentStatus.Pending;
}

export function TaxInfo() {
  const { t, country: translatedCountry } = useTranslate();
  const queryClient = useQueryClient();
  const store = useStore<RootState>();
  const {
    value: isTaxInfoDrawerOpen,
    setTrue: openTaxInfoDrawer,
    setFalse: closeTaxInfoDrawer,
  } = useBoolean(false);
  const {
    value: isTaxSupportingDocsDrawerOpen,
    setTrue: openTaxSupportingDocsDrawer,
    setFalse: closeTaxSupportingDocsDrawer,
  } = useBoolean(false);

  const { requiredDocumentSections, user, documentByDocType } =
    useAppSelector(selectUserData);

  const { data: taxInfo } = useQuery({
    queryKey: ['taxInfo'],
    queryFn: getTaxInfo,
  });

  const isTaxInfoSectionComplete = isTaxInfoComplete(taxInfo);
  const startingQuestionIndex = getTaxInfoStartingQuestionIndex(taxInfo);

  const taxSupportingDocumentSection = requiredDocumentSections.find(
    section => section.type === DocumentSection.TaxSupportingDocument,
  );

  const taxSupportingDocumentSectionForDrawer = useMemo(() => {
    if (!taxSupportingDocumentSection) {
      return undefined;
    }

    const items =
      taxSupportingDocumentSection.groups?.flatMap(group => group.items) ?? [];

    if (!items.length) {
      return taxSupportingDocumentSection;
    }

    return {
      ...taxSupportingDocumentSection,
      displayText: 'documentSection.Tax_Info.title',
      groups: groupTaxSupportingDocumentsByCountry({
        items,
        taxInfo,
        t,
        translatedCountry,
      }),
    };
  }, [t, taxInfo, taxSupportingDocumentSection, translatedCountry]);

  const proofOfEmploymentSection = requiredDocumentSections.find(
    section => section.type === DocumentSection.ProofOfEmployment,
  ) || {
    type: DocumentSection.ProofOfEmployment,
    displayText: 'documentSection.Proof_of_Employment.title',
    status: DocumentStatus.Pending,
    docs: [],
  };

  const basicInfoStatus = useAppSelector(selectBasicInfoStatus);
  const isBasicInfoProcessed =
    basicInfoStatus === DocumentStatus.InReview ||
    basicInfoStatus === DocumentStatus.Approved ||
    basicInfoStatus === DocumentStatus.Rejected;
  const isPOEProcessed =
    proofOfEmploymentSection.status === DocumentStatus.InReview ||
    proofOfEmploymentSection.status === DocumentStatus.Approved ||
    proofOfEmploymentSection.status === DocumentStatus.Rejected;

  const isExc = isExistingCustomer(user);
  const isPersonalDetailsReviewed = Boolean(user?.clientReviewed);
  const isIdentityProcessed = isIdentityCheckComplete(user);
  const isExcGateSatisfied =
    !isExc || (isPersonalDetailsReviewed && isIdentityProcessed);

  const getUploadGuidance = useCallback(
    (docType?: DocumentType) =>
      getTaxSupportingDocumentUploadGuidance({
        docType,
        taxInfo,
        translatedCountry,
      }),
    [taxInfo, translatedCountry],
  );

  const openTaxSupportingDocsIfRequired = useCallback(() => {
    const { requiredDocumentSections: latestSections } = selectUserData(
      store.getState(),
    );
    const latestTaxSupportingSection = latestSections.find(
      section => section.type === DocumentSection.TaxSupportingDocument,
    );

    if (isTaxSupportingDocumentsPending(latestTaxSupportingSection)) {
      openTaxSupportingDocsDrawer();
    }
  }, [openTaxSupportingDocsDrawer, store]);

  const handleCloseTaxInfoDrawer = () => {
    closeTaxInfoDrawer();
    queryClient.invalidateQueries({ queryKey: ['taxInfo'] });
  };

  const handleTaxQuestionsComplete = () => {
    handleCloseTaxInfoDrawer();
    openTaxSupportingDocsIfRequired();
  };

  const handleTaxInfoClick = () => {
    if (!isTaxInfoSectionComplete) {
      openTaxInfoDrawer();
      return;
    }

    if (taxSupportingDocumentSection) {
      openTaxSupportingDocsDrawer();
    }
  };

  const checkBtnDisableForEXC = (
    section: RequiredDocumentSection,
    isNotPendingAndNoDocs: boolean,
  ) => {
    if (!(user as User).clientReviewed) {
      return true;
    }
    if (section.status === DocumentStatus.Approved) {
      return true;
    }
    return isNotPendingAndNoDocs;
  };

  const isTaxSupportingDocsButtonDisabled = (
    section: RequiredDocumentSection,
  ) => {
    if (!user) return true;
    const isNotPendingAndNoDocs =
      section.status !== DocumentStatus.Pending && !section.docs.length;
    if (isExistingCustomer(user)) {
      return checkBtnDisableForEXC(section, isNotPendingAndNoDocs);
    }
    return isNotPendingAndNoDocs;
  };

  const showTaxSupportingDocsStatus =
    isTaxInfoSectionComplete && !!taxSupportingDocumentSection;

  let rowStatus: DocumentStatus | undefined;
  if (showTaxSupportingDocsStatus) {
    rowStatus = taxSupportingDocumentSection.status;
  } else if (isTaxInfoSectionComplete) {
    rowStatus = DocumentStatus.Completed;
  }

  let rowDescription: string | undefined;
  if (!isTaxInfoSectionComplete) {
    rowDescription = t('documentSection.Tax_Info.description');
  } else if (
    showTaxSupportingDocsStatus &&
    taxSupportingDocumentSection.status === DocumentStatus.Pending
  ) {
    rowDescription = t('status.pending');
  }

  const rowRichDescription =
    isTaxInfoSectionComplete && !taxSupportingDocumentSection
      ? t('documentSection.Tax_Info.status.completed')
      : undefined;

  return (
    <>
      <DocumentActionItem
        title={t('documentSection.Tax_Info.title')}
        description={rowDescription}
        richDescription={rowRichDescription}
        status={rowStatus}
        onClick={handleTaxInfoClick}
        enabledForAllStatuses={showTaxSupportingDocsStatus}
        disabled={
          !isBasicInfoProcessed || !isPOEProcessed || !isExcGateSatisfied
        }
        disabledButton={
          showTaxSupportingDocsStatus
            ? isTaxSupportingDocsButtonDisabled(taxSupportingDocumentSection)
            : undefined
        }
      />
      <TaxInfoDrawer
        open={isTaxInfoDrawerOpen}
        onClose={handleCloseTaxInfoDrawer}
        onQuestionsComplete={handleTaxQuestionsComplete}
        startingQuestionIndex={startingQuestionIndex}
      />
      {taxSupportingDocumentSectionForDrawer && (
        <RequiredDocumentSelectDrawer
          open={isTaxSupportingDocsDrawerOpen}
          section={taxSupportingDocumentSectionForDrawer}
          onClose={closeTaxSupportingDocsDrawer}
          documentByDocType={documentByDocType}
          skipSelectWhenSingleDocument
          selectListHeaderKey="documentSection.Tax_Supporting_Document.selectHeader"
          uploadedListHeaderKey="documentSection.Tax_Supporting_Document.uploadedDocuments"
          getUploadGuidance={getUploadGuidance}
        />
      )}
    </>
  );
}

export default React.memo(TaxInfo);
